/**
 * Location capture - spec §11, the real funnel risk.
 *
 * "Share location is one tap. 'Where will you be coming from Thursday?' is a
 *  text field, and text fields are where consumer funnels die."
 *
 * So: GPS is the default even for a future time, and the fallback is offered
 * only when GPS fails or is refused. Privacy must never become a reason to add
 * a tap - the disclosure is one line under the button, not a modal.
 */

export interface Coords {
  lat: number
  lng: number
}

export type GeoFailure =
  | { kind: 'denied' }
  | { kind: 'unavailable' }
  | { kind: 'timeout' }
  | { kind: 'unsupported' }

export function getPosition(timeoutMs = 10_000): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject({ kind: 'unsupported' } satisfies GeoFailure)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        const kind: GeoFailure['kind'] =
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.TIMEOUT
              ? 'timeout'
              : 'unavailable'
        reject({ kind } satisfies GeoFailure)
      },
      {
        // High accuracy costs seconds on mobile and we coarsen to ~1km
        // anyway - precision here would be thrown away server-side.
        enableHighAccuracy: false,
        timeout: timeoutMs,
        maximumAge: 5 * 60 * 1000,
      },
    )
  })
}

/**
 * Remembered origin - §11 point 5: "second use is zero-input, still no
 * account." Stored per-browser only; it never reaches the server as anything
 * but a coarsened geohash.
 */
const REMEMBERED = 'sorted.origin'

export function rememberOrigin(c: Coords, label?: string): void {
  try {
    localStorage.setItem(REMEMBERED, JSON.stringify({ ...c, label, at: Date.now() }))
  } catch {
    // Private mode, or storage disabled. Not worth surfacing.
  }
}

export function recallOrigin(): (Coords & { label?: string }) | null {
  try {
    const raw = localStorage.getItem(REMEMBERED)
    if (!raw) return null
    const v = JSON.parse(raw) as Coords & { label?: string; at: number }
    // A month-old "where you usually are" is worse than asking again.
    if (Date.now() - v.at > 30 * 24 * 60 * 60 * 1000) return null
    return v.label ? { lat: v.lat, lng: v.lng, label: v.label } : { lat: v.lat, lng: v.lng }
  } catch {
    return null
  }
}

/**
 * Where to open a map before the user has said anything.
 *
 * Not a location, a starting view — nothing is submitted from it, so being
 * wrong costs a pan rather than a bad meeting. Best available guess, cheapest
 * first:
 *
 *   1. A remembered origin. She has used this before; it is where she was.
 *   2. The browser's locale and timezone. `de-AT` with `Europe/Vienna` is
 *      Vienna, and it costs nothing and asks nobody.
 *   3. Vienna, where v1's hand-verified coverage is (§16).
 *
 * GPS is deliberately NOT in this list. It is the most accurate answer and the
 * only one that needs a permission prompt, so it belongs behind a tap the user
 * chose — see `locateFromDevice`.
 */
const CITIES: Record<string, Coords> = {
  'Europe/Vienna': { lat: 48.2082, lng: 16.3738 },
  'Europe/Berlin': { lat: 52.52, lng: 13.405 },
  'Europe/Zurich': { lat: 47.3769, lng: 8.5417 },
  'Europe/Prague': { lat: 50.0755, lng: 14.4378 },
  'Europe/Budapest': { lat: 47.4979, lng: 19.0402 },
  'Europe/Bratislava': { lat: 48.1486, lng: 17.1077 },
  'Europe/Munich': { lat: 48.1351, lng: 11.582 },
  'Europe/Paris': { lat: 48.8566, lng: 2.3522 },
  'Europe/London': { lat: 51.5074, lng: -0.1278 },
  'Europe/Madrid': { lat: 40.4168, lng: -3.7038 },
  'Europe/Rome': { lat: 41.9028, lng: 12.4964 },
  'Europe/Amsterdam': { lat: 52.3676, lng: 4.9041 },
  'Europe/Warsaw': { lat: 52.2297, lng: 21.0122 },
  'Europe/Lisbon': { lat: 38.7223, lng: -9.1393 },
  'Europe/Stockholm': { lat: 59.3293, lng: 18.0686 },
  'Europe/Copenhagen': { lat: 55.6761, lng: 12.5683 },
  'America/New_York': { lat: 40.7128, lng: -74.006 },
  'America/Los_Angeles': { lat: 34.0522, lng: -118.2437 },
  'America/Chicago': { lat: 41.8781, lng: -87.6298 },
}

/** Vienna. Where the hand-verified activity coverage is (§16). */
export const HOME: Coords = { lat: 48.2082, lng: 16.3738 }

export function initialMapCentre(): Coords {
  const remembered = recallOrigin()
  if (remembered) return { lat: remembered.lat, lng: remembered.lng }

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const hit = tz ? CITIES[tz] : undefined
    if (hit) return hit
  } catch {
    // Older browser, or a locked-down one. Fall through.
  }

  return HOME
}
